// src/contexts/campaign-data.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Campaign, StorageData, LoadingStatus } from "@/types/metrics";
import { db } from "@/services/db";
import {
  SAMPLE_ACCOUNT_ID,
  SHEET_TABS,
  DATA_STRUCTURE_VERSION,
  SAMPLE_ACCOUNT_SCRIPT_VERSION,
} from "@/lib/constants";
import { useUser } from "@clerk/clerk-react";
import {
  fetchAllData,
  isSheetUnreadable,
  SHEET_UNREADABLE_MESSAGE,
} from "@/lib/data-utils";
import { toast } from "@/components/ui/use-toast";
import { AccountContext } from "./account-context";
import { useLocation } from "react-router-dom";
import {
  generateHierarchicalDataForMetric,
  DataNode as HierarchicalDataNode,
} from "../lib/hierarchical-data-utils";
import {
  AssetPerformanceProcessor,
  AssetAnalysisResult,
} from "@/utils/assetPerformanceProcessor";
import {
  clearAllUserDataForSecurity,
  setupEmergencyClear,
} from "@/utils/security-utils";

const DEBUG = process.env.NODE_ENV === "development";

// Define sales routes where data fetching should be skipped initially
const SALES_ROUTES = new Set([
  "/buy",
  "/build",
  "/pmax",
  "/mastery",
  "/ai", // Membership page
  "/4cs",
  "/cpd",
  "/slides", // Keep in case they are added back or used elsewhere
  "/survey", // Keep in case they are added back or used elsewhere
  "/help", // Help page doesn't need campaign data
  "/feedback", // Feedback page doesn't need campaign data
  "scary",

  "/", // Home page doesn't need campaign data
]);

// Data freshness thresholds in milliseconds
const FRESHNESS_THRESHOLDS = {
  IMMEDIATE_USE: 2 * 60 * 60 * 1000, // 2 hours - use immediately without refresh
  ACTIVE_REFRESH: 8 * 60 * 60 * 1000, // 8 hours - use but actively refresh in background
  FORCE_REFRESH: 24 * 60 * 60 * 1000, // 24 hours - force fresh fetch
} as const;

// Data freshness levels
type DataFreshness = "fresh" | "stale" | "expired";

// Helper function to determine data freshness
function getDataFreshness(timestamp: string): DataFreshness {
  const dataAge = Date.now() - new Date(timestamp).getTime();

  if (dataAge <= FRESHNESS_THRESHOLDS.IMMEDIATE_USE) {
    return "fresh";
  } else if (dataAge <= FRESHNESS_THRESHOLDS.ACTIVE_REFRESH) {
    return "stale";
  } else {
    return "expired";
  }
}

// LRU Cache configuration
const MAX_CACHED_ACCOUNTS = 25; // Keep data for 25 accounts in memory (increased from 10)

// Helper function to implement LRU eviction for account data cache
function evictLeastRecentlyUsed(
  accountData: Record<string, StorageData>,
  recentAccountIds: string[],
  maxAccounts: number = MAX_CACHED_ACCOUNTS,
): Record<string, StorageData> {
  const accountIds = Object.keys(accountData);

  // If we're under the limit, no eviction needed
  if (accountIds.length <= maxAccounts) {
    return accountData;
  }

  // Create a priority order: recent accounts first, then by timestamp (newest first)
  const prioritizedIds = [
    // First, include recent account IDs in order of recency
    ...recentAccountIds.filter((id) => accountData[id]),
    // Then, include any remaining accounts sorted by timestamp (newest first)
    ...accountIds
      .filter((id) => !recentAccountIds.includes(id))
      .sort((a, b) => {
        const timeA = new Date(accountData[a]?.timestamp || 0).getTime();
        const timeB = new Date(accountData[b]?.timestamp || 0).getTime();
        return timeB - timeA; // Newest first
      }),
  ];

  // Keep only the first maxAccounts
  const keepIds = prioritizedIds.slice(0, maxAccounts);
  const evictedIds = accountIds.filter((id) => !keepIds.includes(id));

  // Eviction logging removed to reduce console noise

  // Return new object with only the accounts we want to keep
  return Object.fromEntries(keepIds.map((id) => [id, accountData[id]]));
}

// Define the structure for the processed hierarchical data for all key metrics
export interface ProcessedMetricData {
  nodes: HierarchicalDataNode[];
  accountTotal: number;
}
export interface AllProcessedHierarchicalMetrics {
  impr: ProcessedMetricData | null;
  clicks: ProcessedMetricData | null;
  cost: ProcessedMetricData | null;
  conv: ProcessedMetricData | null;
  value: ProcessedMetricData | null;
}

interface CampaignDataContextType {
  data: StorageData | null;
  accountData: Record<string, StorageData>;
  activeDexieId: string;
  isLoading: boolean;
  loadingStatus: LoadingStatus;
  error?: string;
  lastUpdated: Date | null;
  refreshData: (dexieId: string) => Promise<void>;
  useSampleData: boolean;
  initialCogsAmount?: number;
  initialBreakEvenCpa?: number;
  updateInitialCogsAmount: (value: number) => Promise<void>;
  updateInitialBreakEvenCpa: (value: number) => Promise<void>;
  switchToSampleData: (value: boolean) => Promise<void>;
  currentGameLevel: number;
  updateCurrentGameLevel: (level: number) => Promise<void>;
  thirtyDayData?: Campaign[];
  queueRefresh: (dexieId: string, immediate?: boolean) => void;
  isRefreshing: boolean;
  allProcessedMetrics: AllProcessedHierarchicalMetrics | null;
  isProcessingHierarchy: boolean;
  assetPerformanceAnalysis: AssetAnalysisResult | null;
  isProcessingAssetPerformance: boolean;
  assetPerformanceError?: string;
  isBackgroundRefreshing: boolean;
  backgroundRefreshingAccounts: string[];
  hasDataForAccount: (dexieId: string) => boolean;
  getDataFreshnessForAccount: (dexieId: string) => DataFreshness | null;
}

const CampaignDataContext = createContext<CampaignDataContextType | null>(null);

export function CampaignDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const accountContext = useContext(AccountContext);
  if (!accountContext) {
    throw new Error("CampaignDataProvider must be used within AccountProvider");
  }

  const { activeDexieId, initialized, getRecentAccountIds, allAccounts } =
    accountContext;
  const { user, isSignedIn } = useUser();
  const location = useLocation();

  // Setup emergency clear function for security (development only)
  useEffect(() => {
    if (DEBUG) {
      setupEmergencyClear();
    }
  }, []);

  const [accountData, setAccountData] = useState<Record<string, StorageData>>(
    {},
  );
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("initial");
  const [error, setError] = useState<string | undefined>();
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentGameLevel, setCurrentGameLevel] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [backgroundRefreshingAccounts, setBackgroundRefreshingAccounts] =
    useState<string[]>([]); // Track accounts loading in background
  const refreshQueue = useRef<string[]>([]);
  const isProcessingQueue = useRef(false);

  // Helper function to update account data with LRU eviction
  const setAccountDataWithEviction = useCallback(
    (
      updater: (
        prev: Record<string, StorageData>,
      ) => Record<string, StorageData>,
    ) => {
      setAccountData((prev) => {
        const updated = updater(prev);
        const recentAccountIds = getRecentAccountIds();
        return evictLeastRecentlyUsed(updated, recentAccountIds);
      });
    },
    [getRecentAccountIds],
  );

  // Helper function to check if we have data for an account
  const hasDataForAccount = useCallback(
    (dexieId: string): boolean => {
      return !!accountData[dexieId];
    },
    [accountData],
  );

  // Helper function to get data freshness for an account
  const getDataFreshnessForAccount = useCallback(
    (dexieId: string): DataFreshness | null => {
      const data = accountData[dexieId];
      if (!data || !data.timestamp) return null;
      return getDataFreshness(data.timestamp);
    },
    [accountData],
  );

  // New state for processed hierarchical data
  const [allProcessedMetrics, setAllProcessedMetrics] =
    useState<AllProcessedHierarchicalMetrics | null>(null);
  const [isProcessingHierarchy, setIsProcessingHierarchy] =
    useState<boolean>(false);

  // New state for asset performance analysis (now per dexieId)
  const [assetPerformanceAnalyses, setAssetPerformanceAnalyses] = useState<
    Record<string, AssetAnalysisResult | null>
  >({});
  const [isProcessingAssetPerformance, setIsProcessingAssetPerformance] =
    useState<Record<string, boolean>>({});
  const [assetPerformanceErrors, setAssetPerformanceErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [rawDataTimestamps, setRawDataTimestamps] = useState<
    Record<string, number | undefined>
  >({});
  const [processedAssetTimestamps, setProcessedAssetTimestamps] = useState<
    Record<string, number | undefined>
  >({});

  // Effect to auto-clean data for deleted accounts
  useEffect(() => {
    const accountIdsInData = Object.keys(accountData);
    // Use allAccounts from context which is the single source of truth for existing accounts
    const existingAccountIds = new Set(allAccounts.map((acc) => acc.dexieId));

    const idsToClear = accountIdsInData.filter(
      (id) => !existingAccountIds.has(id),
    );

    if (idsToClear.length > 0) {
      if (DEBUG) {
        console.log(
          "[CampaignDataProvider] Detected deleted or obsolete accounts in memory cache. Clearing data for:",
          idsToClear,
        );
      }
      // Clear from main data cache
      setAccountDataWithEviction((prev) => {
        const newState = { ...prev };
        idsToClear.forEach((id) => {
          delete newState[id];
        });
        return newState;
      });
      // Clear from asset performance cache
      setAssetPerformanceAnalyses((prev) => {
        const newState = { ...prev };
        idsToClear.forEach((id) => {
          delete newState[id];
        });
        return newState;
      });
      // Clear from asset performance errors
      setAssetPerformanceErrors((prev) => {
        const newState = { ...prev };
        idsToClear.forEach((id) => {
          delete newState[id];
        });
        return newState;
      });
    }
  }, [allAccounts, accountData, setAccountDataWithEviction]);

  // This effect handles processing of all data when account data changes
  const processData = useCallback(
    async (dexieId: string, storageData: StorageData) => {
      if (!storageData || !storageData.daily) {
        setAllProcessedMetrics(null);
        return;
      }

      const rawTimestamp = new Date(storageData.timestamp).getTime();

      // Check if we need to re-process using current state
      setProcessedAssetTimestamps((current) => {
        if (current[dexieId] === rawTimestamp) {
          // Already processed, no need to continue
          return current;
        }

        // Mark as processing and continue
        (async () => {
          // Hierarchical data processing logging removed to reduce console noise
          setIsProcessingHierarchy(true);

          try {
            // Use Promise.all to process metrics in parallel
            const [impr, clicks, cost, conv, value] = await Promise.all([
              generateHierarchicalDataForMetric(
                storageData[SHEET_TABS.KEYWORD80] || [],
                storageData[SHEET_TABS.KEYWORD80_PREV] || [],
                "impr",
              ),
              generateHierarchicalDataForMetric(
                storageData[SHEET_TABS.KEYWORD80] || [],
                storageData[SHEET_TABS.KEYWORD80_PREV] || [],
                "clicks",
              ),
              generateHierarchicalDataForMetric(
                storageData[SHEET_TABS.KEYWORD80] || [],
                storageData[SHEET_TABS.KEYWORD80_PREV] || [],
                "cost",
              ),
              generateHierarchicalDataForMetric(
                storageData[SHEET_TABS.KEYWORD80] || [],
                storageData[SHEET_TABS.KEYWORD80_PREV] || [],
                "conv",
              ),
              generateHierarchicalDataForMetric(
                storageData[SHEET_TABS.KEYWORD80] || [],
                storageData[SHEET_TABS.KEYWORD80_PREV] || [],
                "value",
              ),
            ]);

            setAllProcessedMetrics({ impr, clicks, cost, conv, value });
            // Update processed timestamp after successful processing
            setProcessedAssetTimestamps((prev) => ({
              ...prev,
              [dexieId]: rawTimestamp,
            }));
            // Hierarchical data processing success logging removed to reduce console noise
          } catch (err) {
            console.error(
              `[CampaignDataProvider] ❌ Error processing hierarchical data for ${dexieId}:`,
              err,
            );
            setAllProcessedMetrics(null);
          } finally {
            setIsProcessingHierarchy(false);
          }
        })();

        return current;
      });
    },
    [],
  ); // Removed processedAssetTimestamps dependency to prevent infinite loop

  // This effect handles processing of asset data when account data changes
  const processAssetData = useCallback(
    async (dexieId: string, storageData: StorageData) => {
      if (!storageData) {
        setAssetPerformanceAnalyses((prev) => ({ ...prev, [dexieId]: null }));
        return;
      }

      const rawTimestamp = new Date(storageData.timestamp).getTime();

      // Check if we need to re-process using current state
      setRawDataTimestamps((currentTimestamps) => {
        setAssetPerformanceAnalyses((currentAnalyses) => {
          const lastRawTimestamp = currentTimestamps[dexieId];

          // Only re-process if the raw data has changed
          if (lastRawTimestamp === rawTimestamp && currentAnalyses[dexieId]) {
            // Asset performance up-to-date logging removed to reduce console noise
            return currentAnalyses;
          }

          // Start processing asynchronously
          (async () => {
            setIsProcessingAssetPerformance((prev) => ({
              ...prev,
              [dexieId]: true,
            }));
            setAssetPerformanceErrors((prev) => ({
              ...prev,
              [dexieId]: undefined,
            }));

            try {
              // Using a simple check to see if all necessary asset tabs are present
              const requiredAssetTabs = [
                SHEET_TABS.ASSETS,
                SHEET_TABS.PMAX_ASSETS,
                SHEET_TABS.AD_ASSETS,
                SHEET_TABS.CAMP_ASSETS,
                SHEET_TABS.AG_ASSETS,
                SHEET_TABS.SETTINGS, // campaign names
              ];

              const hasAllAssetData = requiredAssetTabs.every(
                (tab) => storageData[tab] && storageData[tab].length > 0,
              );

              if (!hasAllAssetData) {
                setAssetPerformanceAnalyses((prev) => ({
                  ...prev,
                  [dexieId]: null,
                }));
                setAssetPerformanceErrors((prev) => ({
                  ...prev,
                  [dexieId]: "Missing required asset data.",
                }));
                return;
              }
              const analysisResult =
                AssetPerformanceProcessor.processAssetPerformance(storageData);
              setAssetPerformanceAnalyses((prev) => ({
                ...prev,
                [dexieId]: analysisResult,
              }));
              setRawDataTimestamps((prev) => ({
                ...prev,
                [dexieId]: rawTimestamp,
              })); // Update timestamp on successful process
            } catch (err) {
              console.error(
                `[CampaignDataProvider] ❌ Error processing asset performance for ${dexieId}:`,
                err,
              );
              setAssetPerformanceErrors((prev) => ({
                ...prev,
                [dexieId]: err instanceof Error ? err.message : String(err),
              }));
              setAssetPerformanceAnalyses((prev) => ({
                ...prev,
                [dexieId]: null,
              }));
            } finally {
              setIsProcessingAssetPerformance((prev) => ({
                ...prev,
                [dexieId]: false,
              }));
            }
          })();

          return currentAnalyses;
        });
        return currentTimestamps;
      });
    },
    [],
  ); // Removed dependencies to prevent infinite loop

  // Process refresh queue
  const processQueue = useCallback(async () => {
    if (isProcessingQueue.current || !refreshQueue.current.length) return;

    isProcessingQueue.current = true;

    // Only set global refreshing for active account
    const firstDexieId = refreshQueue.current[0];
    const isActiveAccountRefresh = firstDexieId === activeDexieId;

    if (isActiveAccountRefresh) {
      setIsRefreshing(true);
    }

    try {
      while (refreshQueue.current.length > 0) {
        const dexieId = refreshQueue.current[0];
        const account = await db.getAccount(dexieId);

        if (account && !account.isSampleAccount && account.webAppUrl) {
          try {
            // Track background refreshing for non-active accounts
            const isActiveAccount = dexieId === activeDexieId;
            if (!isActiveAccount) {
              setBackgroundRefreshingAccounts((prev) =>
                prev.includes(dexieId) ? prev : [...prev, dexieId],
              );
            }

            // Fetch logging removed to reduce console noise
            const newData = await fetchAllData(dexieId, account.webAppUrl);
            await db.saveAccountData(dexieId, {
              ...newData,
              dexieId,
              accountCID: account.accountCID,
              timestamp: new Date().toISOString(),
              version: DATA_STRUCTURE_VERSION,
            });

            // Update scriptVersion on Account record if changed
            const accountInfo = newData[SHEET_TABS.ACCOUNT]?.[0];
            if (
              accountInfo?.scriptVersion &&
              accountInfo.scriptVersion !== account.scriptVersion
            ) {
              await accountContext.updateAccount({
                ...account,
                scriptVersion: accountInfo.scriptVersion,
              });
            }

            setAccountDataWithEviction((prev) => ({
              ...prev,
              [dexieId]: newData,
            }));
            setRawDataTimestamps((prev) => ({
              ...prev,
              [dexieId]: Date.now(),
            }));

            // Update lastUpdated and loading status only for active account
            if (isActiveAccount) {
              setLastUpdated(new Date());
              setLoadingStatus("idle"); // Immediately set to idle when active account finishes
            }

            // Remove from background refreshing accounts
            if (!isActiveAccount) {
              setBackgroundRefreshingAccounts((prev) =>
                prev.filter((id) => id !== dexieId),
              );
            }
          } catch (err) {
            console.error(
              "Background refresh failed for account:",
              dexieId,
              err,
            );
            // Remove from background refreshing accounts on error too
            setBackgroundRefreshingAccounts((prev) =>
              prev.filter((id) => id !== dexieId),
            );

            // If active account fails, still set to idle to prevent infinite loading
            if (dexieId === activeDexieId) {
              setLoadingStatus("idle");
            }
          }
        }

        refreshQueue.current.shift();
      }
    } finally {
      isProcessingQueue.current = false;
      if (isActiveAccountRefresh) {
        setIsRefreshing(false);
      }
    }
  }, [activeDexieId, setAccountDataWithEviction]);

  // Queue refresh for an account
  const queueRefresh = useCallback(
    (dexieId: string, immediate = false) => {
      if (!refreshQueue.current.includes(dexieId)) {
        if (immediate) {
          refreshQueue.current.unshift(dexieId);
        } else {
          refreshQueue.current.push(dexieId);
        }
        processQueue();
      }
    },
    [processQueue],
  );

  // Handle initial login and background refreshes for multiple accounts (ONLY once per session)
  const hasRunInitialRefreshes = useRef(false);
  useEffect(() => {
    if (isSignedIn && user && initialized && !hasRunInitialRefreshes.current) {
      hasRunInitialRefreshes.current = true; // Prevent multiple runs

      const metadata = (user.unsafeMetadata || {}) as any;
      const lastUsedId = metadata.lastUsedDexieId as string;
      const recentAccountIds = getRecentAccountIds();

      // First, refresh the last used account (if it exists) with priority
      if (lastUsedId) {
        db.getAccount(lastUsedId).then((account) => {
          if (account && !account.isSampleAccount) {
            queueRefresh(lastUsedId, true); // Priority refresh
          }
        });
      }

      // Then, queue background refreshes for other recent accounts
      recentAccountIds.forEach((accountId, index) => {
        if (accountId !== lastUsedId) {
          db.getAccount(accountId).then((account) => {
            if (account && !account.isSampleAccount) {
              // Add a small delay between background refreshes to avoid overwhelming the server
              setTimeout(
                () => {
                  queueRefresh(accountId, false); // Background refresh
                },
                (index + 1) * 5000,
              ); // 5 second delays between each (increased from 2s)
            }
          });
        }
      });
    }
  }, [isSignedIn, user, initialized, queueRefresh, getRecentAccountIds]);

  // Helper function to load data for a specific account (moved outside effect)
  const loadAccountData = useCallback(
    async (dexieId: string, isActiveAccount: boolean) => {
      try {
        const account = await db.getAccount(dexieId);
        if (!account) {
          if (DEBUG) console.warn(`Account not found during load: ${dexieId}`);
          return;
        }

        if (account.isSampleAccount) {
          const { loadSampleData } = await import("@/lib/sample-data-utils");
          const sampleData = await loadSampleData();
          sampleData.version = DATA_STRUCTURE_VERSION; // Stamp with version

          setAccountDataWithEviction((prev) => ({
            ...prev,
            [dexieId]: sampleData,
          }));
          if (isActiveAccount) {
            setLastUpdated(new Date(sampleData.timestamp));
            setLoadingStatus("idle");
          }
        } else {
          const storedData = await db.getAccountData(dexieId);

          if (storedData && storedData.version === DATA_STRUCTURE_VERSION) {
            const freshness = getDataFreshness(storedData.timestamp);

            if (DEBUG) {
              console.log(
                `[CampaignDataProvider] 📁 Loading data from IndexedDB for ${dexieId}:`,
                {
                  freshness,
                  timestamp: storedData.timestamp,
                  dataSize: JSON.stringify(storedData).length / 1024 + "KB",
                },
              );
            }

            // Always load existing data first for immediate display
            setAccountDataWithEviction((prev) => ({
              ...prev,
              [dexieId]: storedData,
            }));
            setRawDataTimestamps((prev) => ({
              ...prev,
              [dexieId]: new Date(storedData.timestamp).getTime(),
            }));

            if (isActiveAccount) {
              setLastUpdated(new Date(storedData.timestamp));
            }

            // Apply tiered refresh strategy based on data freshness - BUT ONLY for active account when switching
            if (isActiveAccount && freshness === "expired") {
              // Don't show loading state for expired data - use cached data immediately
              queueRefresh(dexieId, false); // Background refresh only
            } else if (isActiveAccount && freshness === "stale") {
              queueRefresh(dexieId, false); // Background refresh - don't block UI
            }

            if (isActiveAccount) {
              setLoadingStatus("idle"); // Always set to idle for active account if we have data
            }
          } else {
            if (storedData) {
              console.warn(
                `Data version mismatch for ${dexieId}. Got ${storedData.version}, expected ${DATA_STRUCTURE_VERSION}. Forcing refresh.`,
              );
            }

            // For active accounts with no data, load immediately instead of queuing
            if (isActiveAccount) {
              setLoadingStatus("refresh"); // Show loading state

              try {
                const account = await db.getAccount(dexieId);
                if (account && !account.isSampleAccount && account.webAppUrl) {
                  // Fresh data fetching logging removed to reduce console noise
                  const newData = await fetchAllData(
                    dexieId,
                    account.webAppUrl,
                  );
                  await db.saveAccountData(dexieId, {
                    ...newData,
                    dexieId,
                    accountCID: account.accountCID,
                    timestamp: new Date().toISOString(),
                    version: DATA_STRUCTURE_VERSION,
                  });

                  setAccountDataWithEviction((prev) => ({
                    ...prev,
                    [dexieId]: newData,
                  }));
                  setRawDataTimestamps((prev) => ({
                    ...prev,
                    [dexieId]: Date.now(),
                  }));
                  setLastUpdated(new Date());
                  setLoadingStatus("idle");
                } else {
                  throw new Error("Account not found or missing webAppUrl");
                }
              } catch (err) {
                console.error(
                  "Failed to load data immediately for active account:",
                  err,
                );
                // Fall back to queuing if immediate load fails
                queueRefresh(dexieId, true); // Priority refresh as fallback
              }
            } else {
              queueRefresh(dexieId, false); // Background refresh for non-active accounts
            }
          }
        }
      } catch (err) {
        console.error(`Failed to load data for account ${dexieId}:`, err);
        if (isActiveAccount) {
          setError(err instanceof Error ? err.message : "Failed to load data");
          setLoadingStatus("idle");
        }
      }
    },
    [setAccountDataWithEviction, queueRefresh],
  );

  // Restore recently used accounts from IndexedDB on app initialization - SYNCHRONOUSLY FIRST
  const hasRestoredCache = useRef(false);
  useEffect(() => {
    if (!initialized || hasRestoredCache.current || !isSignedIn || !user)
      return;

    const restoreAccountsCache = async () => {
      try {
        hasRestoredCache.current = true;
        const recentAccountIds = getRecentAccountIds();

        // Cache restoration logging removed to reduce console noise

        // Load cached data for recent accounts from IndexedDB
        const restoredData: Record<string, StorageData> = {};
        const accountsToLoad: string[] = [];

        // Load ALL recent accounts synchronously first
        for (const accountId of recentAccountIds.slice(
          0,
          MAX_CACHED_ACCOUNTS,
        )) {
          try {
            const cachedData = await db.getAccountData(accountId);
            if (cachedData && cachedData.version === DATA_STRUCTURE_VERSION) {
              // For sample account, check script version
              let isValid = true;
              if (accountId === SAMPLE_ACCOUNT_ID) {
                const cachedAccountInfo = cachedData[SHEET_TABS.ACCOUNT]?.[0];
                const hasCorrectScriptVersion =
                  cachedAccountInfo?.scriptVersion ===
                  SAMPLE_ACCOUNT_SCRIPT_VERSION;

                if (!hasCorrectScriptVersion) {
                  if (DEBUG) {
                    console.log(
                      `[CampaignDataProvider] ❌ Sample data script version mismatch for ${accountId}. Current: ${cachedAccountInfo?.scriptVersion}, Expected: ${SAMPLE_ACCOUNT_SCRIPT_VERSION}`,
                    );
                  }
                  isValid = false;
                }
              }

              if (isValid) {
                const freshness = getDataFreshness(cachedData.timestamp);
                restoredData[accountId] = cachedData;
                // Restoration logging removed to reduce console noise
              } else {
                accountsToLoad.push(accountId);
              }
            } else {
              // No valid cached data logging removed to reduce console noise
              accountsToLoad.push(accountId);
            }
          } catch (err) {
            // Failed restore logging removed to reduce console noise
            accountsToLoad.push(accountId);
          }
        }

        // IMMEDIATELY update memory with restored data before any other loading logic runs
        if (Object.keys(restoredData).length > 0) {
          setAccountDataWithEviction((prev) => ({ ...prev, ...restoredData }));

          // Set rawDataTimestamps for restored accounts
          const restoredTimestamps: Record<string, number> = {};
          Object.keys(restoredData).forEach((accountId) => {
            const data = restoredData[accountId];
            if (data.timestamp) {
              restoredTimestamps[accountId] = new Date(
                data.timestamp,
              ).getTime();
            }
          });
          setRawDataTimestamps((prev) => ({ ...prev, ...restoredTimestamps }));

          // Memory restored logging removed to reduce console noise
        }

        // Only queue fresh fetches for accounts that don't have cached data
        if (accountsToLoad.length > 0) {
          // Fresh data fetch logging removed to reduce console noise
          // Queue loading for accounts without cached data, with delays to avoid overwhelming
          accountsToLoad.forEach((accountId, index) => {
            setTimeout(
              () => {
                queueRefresh(accountId, false); // Background refresh
              },
              (index + 1) * 2000,
            ); // 2 second delays between each
          });
        }
      } catch (err) {
        console.error(
          "[CampaignDataProvider] Failed to restore account cache:",
          err,
        );
      }
    };

    // Run cache restoration IMMEDIATELY and WAIT for it to complete
    restoreAccountsCache();
  }, [
    initialized,
    isSignedIn,
    user,
    getRecentAccountIds,
    setAccountDataWithEviction,
    queueRefresh,
  ]);

  // Load data from Dexie when active account changes
  useEffect(() => {
    if (!initialized || !activeDexieId) return;

    const loadInitialData = async () => {
      // Active account change logging removed to reduce console noise

      // If we're on a sales page, don't fetch data initially
      if (SALES_ROUTES.has(location.pathname)) {
        // Sales route logging removed to reduce console noise
        setLoadingStatus("idle");
        return;
      }

      setLoadingStatus("refresh");
      setError(undefined);

      // Try to load from memory cache first
      if (accountData[activeDexieId]) {
        // Memory cache loading removed to reduce console noise
        setLastUpdated(new Date(accountData[activeDexieId].timestamp));
        setLoadingStatus("idle");
      } else {
        // IndexedDB loading logging removed to reduce console noise
        const dataFromDb = await db.getAccountData(activeDexieId);
        if (dataFromDb) {
          // Data found logging removed to reduce console noise
          setAccountDataWithEviction((prev) => ({
            ...prev,
            [activeDexieId]: dataFromDb,
          }));
          setLastUpdated(new Date(dataFromDb.timestamp));
          setLoadingStatus("idle");

          await processData(activeDexieId, dataFromDb);
          await processAssetData(activeDexieId, dataFromDb);
          return;
        }

        if (activeDexieId === SAMPLE_ACCOUNT_ID) {
          // Sample data loading logging removed to reduce console noise

          // Check if we have cached sample data and validate script version
          const cachedSampleData = await db.getAccountData(activeDexieId);
          let shouldRefreshSampleData = true;

          if (cachedSampleData) {
            // Check if cached sample data has correct version and script version
            const hasCorrectVersion =
              cachedSampleData.version === DATA_STRUCTURE_VERSION;
            const cachedAccountInfo = cachedSampleData[SHEET_TABS.ACCOUNT]?.[0];
            const hasCorrectScriptVersion =
              cachedAccountInfo?.scriptVersion ===
              SAMPLE_ACCOUNT_SCRIPT_VERSION;

            if (hasCorrectVersion && hasCorrectScriptVersion) {
              // Cached sample data logging removed to reduce console noise
              shouldRefreshSampleData = false;

              setAccountDataWithEviction((prev) => ({
                ...prev,
                [activeDexieId]: cachedSampleData,
              }));
              setLastUpdated(new Date(cachedSampleData.timestamp));
              setLoadingStatus("idle");

              await processData(activeDexieId, cachedSampleData);
              await processAssetData(activeDexieId, cachedSampleData);
            } else {
              // Sample data refresh logging removed to reduce console noise
            }
          }

          if (shouldRefreshSampleData) {
            // Fresh sample data loading logging removed to reduce console noise
            // Clear cached sample data first to force fresh load
            await db.deleteAccountData(activeDexieId);
            const { loadSampleData } = await import("@/lib/sample-data-utils");
            const sampleData = await loadSampleData();
            sampleData.version = DATA_STRUCTURE_VERSION; // Stamp with version
            await db.saveAccountData(activeDexieId, sampleData);

            setAccountDataWithEviction((prev) => ({
              ...prev,
              [activeDexieId]: sampleData,
            }));
            setLastUpdated(new Date(sampleData.timestamp));
            setLoadingStatus("idle");

            await processData(activeDexieId, sampleData);
            await processAssetData(activeDexieId, sampleData);
          }
        } else {
          // No data found logging removed to reduce console noise
          // If no data, queue a refresh immediately
          queueRefresh(activeDexieId, true);
        }
      }
    };

    loadInitialData();
  }, [
    activeDexieId,
    initialized,
    location.pathname,
    // accountData removed from deps - would cause infinite loop when data updates
    setAccountDataWithEviction,
    queueRefresh,
  ]);

  // This effect handles background refreshing based on data freshness
  useEffect(() => {
    if (!initialized || !activeDexieId) return;

    const checkAndRefresh = () => {
      const data = accountData[activeDexieId];
      if (!data) return;

      const freshness = getDataFreshness(data.timestamp);
      if (freshness === "stale" || freshness === "expired") {
        // Data freshness logging removed to reduce console noise
        queueRefresh(activeDexieId);
      }
    };

    // Check on mount and then periodically
    checkAndRefresh();
    const interval = setInterval(checkAndRefresh, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [activeDexieId, initialized, accountData, queueRefresh]);

  // This is the function that will be called to refresh the data
  const refreshData = async (dexieId: string) => {
    // Don't set loading status for individual account refreshes
    // setLoadingStatus('refresh')

    let fetchedData: StorageData | undefined;

    try {
      const account = await db.getAccount(dexieId);
      if (!account) throw new Error("Account not found");

      if (account.isSampleAccount) {
        // Sample data refresh logging removed to reduce console noise
        // Clear cached sample data first to force fresh load
        await db.deleteAccountData(dexieId);
        const { loadSampleData } = await import("@/lib/sample-data-utils");
        const sampleData = await loadSampleData();
        sampleData.version = DATA_STRUCTURE_VERSION; // Stamp with version
        await db.saveAccountData(dexieId, sampleData);
        setAccountDataWithEviction((prev) => ({
          ...prev,
          [dexieId]: sampleData,
        }));
        setLastUpdated(new Date(sampleData.timestamp));
      } else {
        // Fetch all data
        const newData = await fetchAllData(dexieId, account.webAppUrl);

        // Get script version from account data
        const accountInfo = newData[SHEET_TABS.ACCOUNT]?.[0];
        if (
          accountInfo?.scriptVersion &&
          accountInfo.scriptVersion !== account.scriptVersion
        ) {
          console.log(
            "Updating account script version:",
            accountInfo.scriptVersion,
          );
          await accountContext.updateAccount({
            ...account,
            scriptVersion: accountInfo.scriptVersion,
          });
        }

        // Create the data object with metadata for saving to DB
        const dataToSave = {
          ...newData,
          dexieId,
          accountCID: account.accountCID,
          timestamp: new Date().toISOString(),
          version: DATA_STRUCTURE_VERSION,
        };

        // Save to database
        await db.saveAccountData(dexieId, dataToSave);
        fetchedData = dataToSave;

        // Update raw data timestamps
        setRawDataTimestamps((prev) => ({ ...prev, [dexieId]: Date.now() }));

        // Immediately update the state with the fresh data (don't re-fetch from DB)
        setAccountDataWithEviction((prev) => ({
          ...prev,
          [dexieId]: dataToSave,
        }));
      }

      setLastUpdated(new Date());

      // Nothing at all came back from the sheet. Say so, rather than leaving
      // an account showing 0 records everywhere as if it were simply empty.
      if (isSheetUnreadable(fetchedData)) {
        setError(SHEET_UNREADABLE_MESSAGE);
        toast({
          variant: "destructive",
          title: "Couldn't read your Google Sheet",
          description: SHEET_UNREADABLE_MESSAGE,
        });
        return;
      }

      toast({ title: "Data refreshed successfully!" });
    } catch (err) {
      console.error("Manual refresh failed:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh data");
      toast({
        variant: "destructive",
        title: "Refresh Failed",
        description:
          err instanceof Error
            ? err.message
            : "Could not refresh data from source.",
      });
    } finally {
      // setLoadingStatus('idle') // Don't change global status
    }
  };

  // Handle user sign out - clear all cached data for security
  useEffect(() => {
    if (!isSignedIn) {
      // User sign out logging removed to reduce console noise

      // Clear all account data from memory immediately
      setAccountData({});
      setRawDataTimestamps({});
      setProcessedAssetTimestamps({});
      setAssetPerformanceAnalyses({});
      setIsProcessingAssetPerformance({});
      setAssetPerformanceErrors({});
      setAllProcessedMetrics(null);
      setError(undefined);
      setLastUpdated(null);
      setCurrentGameLevel(0);
      setIsRefreshing(false);
      setBackgroundRefreshingAccounts([]);

      // Clear refresh queue
      refreshQueue.current = [];
      isProcessingQueue.current = false;

      // Reset processing refs
      hasRunInitialRefreshes.current = false;
      hasRestoredCache.current = false;

      // Use comprehensive security clearing function
      clearAllUserDataForSecurity().catch((err) => {
        console.error("[CampaignDataProvider] Security clear failed:", err);
      });
    }
  }, [isSignedIn]);

  // Load game level from Clerk for signed-in users
  useEffect(() => {
    if (isSignedIn && user) {
      const clerkLevel = user.unsafeMetadata.currentGameLevel;
      if (typeof clerkLevel === "number") {
        setCurrentGameLevel(clerkLevel);
      }
    }
  }, [isSignedIn, user]);

  const updateCurrentGameLevel = useCallback(
    async (level: number) => {
      // Optimistically update UI first
      setCurrentGameLevel(level);

      // Only persist for signed-in users
      if (isSignedIn && user) {
        try {
          await user.update({
            unsafeMetadata: {
              ...user.unsafeMetadata,
              currentGameLevel: level,
            },
          });
        } catch (err) {
          // On error, revert the optimistic update
          setCurrentGameLevel(currentGameLevel);
          console.error("Failed to update game level:", err);
          throw err;
        }
      }
    },
    [isSignedIn, user, currentGameLevel],
  );

  const contextValue = useMemo(
    () => ({
      data: activeDexieId ? (accountData[activeDexieId] ?? null) : null,
      accountData,
      activeDexieId,
      isLoading: loadingStatus !== "idle",
      loadingStatus,
      error,
      lastUpdated,
      refreshData,
      useSampleData: activeDexieId === SAMPLE_ACCOUNT_ID,
      initialCogsAmount: 0,
      initialBreakEvenCpa: 0,
      updateInitialCogsAmount: async () => {},
      updateInitialBreakEvenCpa: async () => {},
      switchToSampleData: async () => {},
      currentGameLevel,
      updateCurrentGameLevel,
      thirtyDayData: (activeDexieId && accountData[activeDexieId])?.[SHEET_TABS.THIRTY_DAYS] ?? [],
      queueRefresh,
      isRefreshing,
      allProcessedMetrics,
      isProcessingHierarchy,
      assetPerformanceAnalysis: activeDexieId
        ? assetPerformanceAnalyses[activeDexieId]
        : null,
      isProcessingAssetPerformance: activeDexieId
        ? !!isProcessingAssetPerformance[activeDexieId]
        : false,
      assetPerformanceError: activeDexieId
        ? assetPerformanceErrors[activeDexieId]
        : undefined,
      isBackgroundRefreshing: backgroundRefreshingAccounts.length > 0,
      backgroundRefreshingAccounts,
      hasDataForAccount,
      getDataFreshnessForAccount,
      rawDataTimestamps,
      processedAssetTimestamps,
    }),
    [
      activeDexieId,
      accountData,
      loadingStatus,
      error,
      lastUpdated,
      currentGameLevel,
      isRefreshing,
      allProcessedMetrics,
      isProcessingHierarchy,
      assetPerformanceAnalyses,
      isProcessingAssetPerformance,
      assetPerformanceErrors,
      backgroundRefreshingAccounts,
      hasDataForAccount,
      getDataFreshnessForAccount,
      rawDataTimestamps,
      processedAssetTimestamps,
    ],
  );

  // Always provide the context to avoid "useCampaignData must be used within CampaignDataProvider" errors.
  // Block children from rendering until we have either data or a confirmed idle state with no data.
  const hasData = activeDexieId ? !!accountData[activeDexieId] : false;
  // Also check if we're on a non-sales route that needs data but hasn't loaded it yet.
  // This covers the race condition where "/" sets idle status, then redirect to "/trends" renders
  // children before the loadInitialData effect re-runs for the new route.
  const isOnSalesRoute = SALES_ROUTES.has(location.pathname);
  const showLoadingScreen = !initialized || (loadingStatus !== "idle" && !hasData) || (!hasData && !isOnSalesRoute && !!activeDexieId && loadingStatus === "idle" && !error);

  return (
    <CampaignDataContext.Provider value={contextValue}>
      {showLoadingScreen ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">
            {error ? "Error loading data" : "Loading data..."}
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
        </div>
      ) : (
        children
      )}
    </CampaignDataContext.Provider>
  );
}

export function useCampaignData() {
  const context = useContext(CampaignDataContext);
  if (!context) {
    throw new Error("useCampaignData must be used within CampaignDataProvider");
  }
  return context;
}
